/*
 * Factual claims made on the public marketing site.
 *
 * Everything here is a statement about the product that a visitor may act on,
 * so it lives in one file rather than being scattered through the markup.
 *
 * Two rules:
 *   1. Prices here are MARKETING COPY ONLY. GET /api/web/pricing requires a
 *      JWT, so a logged-out page cannot read it. Keep these numbers in step
 *      with webauth/pricing.py by hand. The in-app pricing screen (Stage 3)
 *      always uses the live endpoint and never these values.
 *   2. Accuracy figures are public performance claims. Verify them against real
 *      graded results before launch, and re-check whenever they are updated.
 */

/**
 * Headline figures.
 *
 * Deliberately contains NO accuracy percentage. Accuracy is a public
 * performance claim, so it is only ever rendered from live data fetched via
 * GET /api/public/accuracy (see src/api/public.js). Until that endpoint ships,
 * the track-record block presents accuracy as "tracked live" with no number
 * rather than showing an invented one.
 *
 * Sport and language counts are stable product facts and are safe here.
 */
export const MARKETING_STATS = {
  sportsCount: 9,
  languagesCount: 4,
};

/** Free-plan allowance, matching the bot's own copy ("free_limit_note"). */
export const FREE_FORECAST_ALLOWANCE = '1 forecast every 3 days';

/**
 * Tiers as shown on the public pricing section.
 *
 * Names, prices and features mirror webauth/pricing.py and the bot's
 * subscription copy. VIP is deliberately absent: it delivers a private Telegram
 * channel invite, which a web-only account cannot receive, and the backend
 * rejects it with a 400.
 */
export const MARKETING_TIERS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: '',
    features: [
      `${FREE_FORECAST_ALLOWANCE[0].toUpperCase()}${FREE_FORECAST_ALLOWANCE.slice(1)}`,
      'All 9 sports and every major league',
      'Live accuracy track record',
    ],
    cta: 'Get started',
    featured: false,
  },
  {
    id: 'basic',
    name: 'Basic',
    price: '$9.99',
    period: '/mo',
    features: [
      'Unlimited match forecasts',
      'All supported sports and leagues',
      '30-day forecast history',
    ],
    cta: 'Choose Basic',
    featured: false,
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '$24.99',
    period: '/mo',
    features: [
      'Everything in Basic',
      'Daily Top Picks digest',
      'On-demand Top Picks',
      'Matches with 70%+ win probability',
    ],
    cta: 'Choose Premium',
    featured: true,
  },
];

/** The nine covered sports, in the prototype's display order. */
export const SPORTS = [
  { emoji: '⚽', name: 'Soccer' },
  { emoji: '🏀', name: 'Basketball' },
  { emoji: '🏈', name: 'Am. Football' },
  { emoji: '🤾', name: 'Handball' },
  { emoji: '🏒', name: 'Hockey' },
  { emoji: '🥊', name: 'MMA' },
  { emoji: '🏉', name: 'Rugby' },
  { emoji: '🏐', name: 'Volleyball' },
  { emoji: '🏏', name: 'Cricket' },
];

/** FAQ entries. Also emitted as FAQPage structured data for search results. */
export const FAQ = [
  {
    q: "How accurate are Don Quack's forecasts?",
    a: 'We grade every Top Pick once the game ends and publish the running accuracy — no cherry-picking. You can see the current figure and the breakdown by sport on the track record page.',
  },
  {
    q: 'Is this betting advice?',
    a: 'No. Don Quack provides AI-generated probability forecasts for entertainment and information. They are not financial or betting advice, and outcomes are never guaranteed. Always bet responsibly.',
  },
  {
    q: 'Which sports and languages do you cover?',
    a: 'Nine sports — soccer, basketball, American football, handball, hockey, MMA, rugby, volleyball, and cricket — in English, Russian, Spanish, and Portuguese.',
  },
  {
    q: 'Do I need to download anything?',
    a: "No. Don Quack runs in your browser, and there's also a Telegram bot if you prefer. Signing up takes a few seconds with just your email — we send you a 6-digit code, so there's no password to remember.",
  },
  {
    q: 'How do payments work?',
    a: 'Subscriptions are paid with crypto through a simple, private checkout. Pick any currency the checkout supports. You can change or stop your plan anytime from your account.',
  },
];
